import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDateTimePicker as DateTimePicker } from '../../shared/components/forms/HotelDatePicker';
import api, { getBookingDetails } from '../../services/api';
import { formatPaymentTime, toPaymentProcessedAtIso } from '../../utils/dateUtils';
import {
  formatBookingCurrency,
  getBookingCurrencySymbol,
  getPaymentSummaryData,
  parsePaymentsApiResponse,
  round2,
} from '../../utils/bookingPaymentSummary';
import BookingPaymentSummaryCard from './BookingPaymentSummaryCard';
import BookingFinancialHistoryPanel from './BookingFinancialHistoryPanel';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerInputBg,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
  createBookingDrawerOverlayProps,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import PaymentDrawerHeaderTitle from './PaymentDrawerHeaderTitle';
import PaymentLinkDrawerOverlay from './PaymentLinkDrawerOverlay';
import {
  filterPaymentMethodsForComm,
  PAYMENT_LINK_METHOD,
  useBookingPaymentLinkComm,
} from '../../hooks/useBookingPaymentLinkComm';
import { PAYMENT_METHOD_CHIPS } from '../../utils/paymentMethodUtils';
import { canModifyBooking } from '../../utils/featurePermissions';
import { PERMISSION_DENIED_MESSAGE } from '../../auth/permissions';

const ACCENT = BOOKING_DRAWER_ACCENT;

function resetPaymentForm(setters) {
  const {
    setPaymentAmount,
    setPaymentDate,
    setPaymentMethod,
    setPaymentLinkSendOpen,
    setReferenceNumber,
    setPaymentReceipt,
    setPaymentCameraUpload,
    setPaymentError,
  } = setters;
  setPaymentAmount(0);
  setPaymentDate(moment());
  setPaymentMethod('');
  setPaymentLinkSendOpen(false);
  setReferenceNumber('');
  setPaymentReceipt(null);
  setPaymentCameraUpload(false);
  setPaymentError('');
}

export default function BookingAddPaymentDrawer({
  open,
  onClose,
  bookingId,
  bookingContext,
  paymentHistory: initialPaymentHistory = [],
  isDark,
  onPaymentAdded,
}) {
  const drawerTheme = getBookingDrawerTheme(Boolean(isDark));
  const { datePickerSlotProps } = useMemo(
    () => createBookingDrawerOverlayProps(Boolean(isDark)),
    [isDark],
  );

  const [booking, setBooking] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(() => moment());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentCameraUpload, setPaymentCameraUpload] = useState(false);
  const [paymentCameraOpen, setPaymentCameraOpen] = useState(false);
  const [paymentStream, setPaymentStream] = useState(null);
  const [paymentLinkSendOpen, setPaymentLinkSendOpen] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [successMessage, setSuccessMessage] = useState(null);
  const [cameraPermissionHint, setCameraPermissionHint] = useState({ open: false, message: '' });
  const canUpdatePayment = canModifyBooking();

  const paymentReceiptFileInputRef = useRef(null);
  const paymentReceiptVideoRef = useRef(null);
  const paymentReceiptCanvasRef = useRef(null);
  const drawerRootRef = useRef(null);

  const {
    canSendPaymentLink,
    canSendPaymentLinkEmail,
    canSendPaymentLinkWhatsApp,
  } = useBookingPaymentLinkComm();

  const paymentMethods = useMemo(
    () => filterPaymentMethodsForComm(PAYMENT_METHOD_CHIPS, canSendPaymentLink),
    [canSendPaymentLink]
  );

  const safeBookingId = useMemo(() => {
    const parsed = Number(bookingId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [bookingId]);

  const displayBooking = useMemo(() => {
    if (booking) return booking;
    if (!safeBookingId && !bookingContext) return null;
    return {
      id: safeBookingId,
      booking_id: safeBookingId,
      booking_code: bookingContext?.booking_code,
      code: bookingContext?.booking_code,
      invoice_number: bookingContext?.invoice_number,
      guest_name: bookingContext?.guest_name,
      currency: bookingContext?.currency,
    };
  }, [booking, safeBookingId, bookingContext]);
  const displayCurrencySymbol = getBookingCurrencySymbol(displayBooking?.currency || 'INR');

  const summary = useMemo(
    () => getPaymentSummaryData(displayBooking || {}, paymentHistory),
    [displayBooking, paymentHistory]
  );

  const formSetters = useMemo(
    () => ({
      setPaymentAmount,
      setPaymentDate,
      setPaymentMethod,
      setPaymentLinkSendOpen,
      setReferenceNumber,
      setPaymentReceipt,
      setPaymentCameraUpload,
      setPaymentError,
    }),
    []
  );

  const fetchPaymentHistory = useCallback(async (bookingCode) => {
    if (!bookingCode) return;
    setLoadingPayments(true);
    try {
      const response = await api.get('/finance/api/payments/', {
        params: { booking_code: bookingCode },
      });
      setPaymentHistory(parsePaymentsApiResponse(response.data));
    } catch {
      setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  }, []);

  const fetchBooking = useCallback(async () => {
    if (!safeBookingId) return;
    setLoadingBooking(true);
    try {
      const data = await getBookingDetails(safeBookingId);
      setBooking(data);
      const code = data?.code || data?.booking_code || bookingContext?.booking_code;
      if (code) {
        await fetchPaymentHistory(code);
      }
    } catch {
      setBooking(null);
      if (bookingContext?.booking_code) {
        await fetchPaymentHistory(bookingContext.booking_code);
      }
    } finally {
      setLoadingBooking(false);
    }
  }, [safeBookingId, bookingContext?.booking_code, fetchPaymentHistory]);

  useEffect(() => {
    if (!canSendPaymentLink && paymentMethod === PAYMENT_LINK_METHOD) {
      setPaymentMethod('');
      setPaymentLinkSendOpen(false);
    }
  }, [canSendPaymentLink, paymentMethod]);

  useEffect(() => {
    if (!open) return;
    setPaymentHistory(Array.isArray(initialPaymentHistory) ? initialPaymentHistory : []);
    fetchBooking();
  }, [open, safeBookingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !displayBooking) return;
    const remaining = getPaymentSummaryData(displayBooking, paymentHistory).remainingAmount;
    if (remaining !== undefined && remaining !== null && remaining > 0) {
      setPaymentAmount(round2(remaining));
    } else {
      setPaymentAmount(0);
    }
  }, [open, displayBooking, paymentHistory]);

  useEffect(() => () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach((track) => track.stop());
    }
  }, [paymentStream]);

  const handleClose = () => {
    if (submittingPayment) return;
    if (paymentStream) {
      paymentStream.getTracks().forEach((track) => track.stop());
      setPaymentStream(null);
    }
    resetPaymentForm(formSetters);
    setPaymentCameraOpen(false);
    onClose?.();
  };

  const startPaymentCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraPermissionHint({
        open: true,
        message: 'Camera is not available. Turn off Camera upload to upload a receipt image from files.',
      });
      return;
    }
    try {
      if (paymentStream) {
        paymentStream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setPaymentStream(mediaStream);
      setPaymentCameraOpen(true);
      setTimeout(() => {
        if (paymentReceiptVideoRef.current) {
          paymentReceiptVideoRef.current.srcObject = mediaStream;
          paymentReceiptVideoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch {
      setCameraPermissionHint({
        open: true,
        message:
          'Camera access was denied or is unavailable. Allow camera permission in your browser settings, or turn off Camera upload to choose a file.',
      });
    }
  };

  const stopPaymentCamera = () => {
    if (paymentStream) {
      paymentStream.getTracks().forEach((track) => track.stop());
      setPaymentStream(null);
    }
    setPaymentCameraOpen(false);
  };

  const capturePaymentReceipt = () => {
    if (paymentReceiptVideoRef.current && paymentReceiptCanvasRef.current) {
      const video = paymentReceiptVideoRef.current;
      const canvas = paymentReceiptCanvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      setPaymentReceipt(canvas.toDataURL('image/jpeg', 0.8));
      stopPaymentCamera();
    }
  };

  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => setPaymentReceipt(event.target.result);
      reader.readAsDataURL(file);
    } else {
      setPaymentError('Please upload an image file.');
    }
  };

  const handlePaymentReceiptClick = () => {
    if (paymentCameraUpload) {
      startPaymentCamera();
    } else {
      paymentReceiptFileInputRef.current?.click();
    }
  };

  const handleSubmitPayment = async () => {
    if (!safeBookingId) return;
    if (!canUpdatePayment) {
      setPaymentError(PERMISSION_DENIED_MESSAGE);
      return;
    }
    const amountInput = String(paymentAmount ?? '').trim();
    const amountValue = Number(amountInput);
    if (!amountInput || !Number.isFinite(amountValue) || amountValue <= 0) {
      setPaymentError('Please enter a valid payment amount.');
      return;
    }
    if (!paymentMethod) {
      setPaymentError('Please select a payment method.');
      return;
    }
    if (paymentMethod === PAYMENT_LINK_METHOD) {
      setPaymentError('Use Email, WhatsApp, or Both in the payment link panel.');
      return;
    }

    setSubmittingPayment(true);
    setPaymentError('');
    try {
      const processedAtIso = toPaymentProcessedAtIso(paymentDate);
      let response;
      if (paymentReceipt) {
        const formData = new FormData();
        formData.append('booking', safeBookingId);
        formData.append('amount', amountInput);
        formData.append('payment_type', 'ADVANCE');
        formData.append('payment_method', paymentMethod);
        if (referenceNumber) formData.append('reference_number', referenceNumber);
        formData.append('status', 'SUCCESS');
        if (processedAtIso) formData.append('processed_at', processedAtIso);

        if (paymentReceipt.startsWith('data:image')) {
          const blobResponse = await fetch(paymentReceipt);
          const blob = await blobResponse.blob();
          formData.append('document', blob, 'payment_receipt.jpg');
        } else if (paymentReceipt instanceof File) {
          formData.append('document', paymentReceipt);
        }

        response = await api.post('/finance/api/payments/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await api.post('/finance/api/payments/', {
          booking: safeBookingId,
          amount: amountInput,
          payment_type: 'ADVANCE',
          payment_method: paymentMethod,
          reference_number: referenceNumber || null,
          status: 'SUCCESS',
          processed_at: processedAtIso,
        });
      }

      if (response.data) {
        const createdPayment = response.data;
        const paidDelta = Number(createdPayment?.amount ?? amountValue ?? 0);
        setPaymentHistory((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (createdPayment?.id && list.some((row) => Number(row?.id) === Number(createdPayment.id))) {
            return list;
          }
          return createdPayment ? [createdPayment, ...list] : list;
        });
        setBooking((prev) => {
          if (!prev || !Number.isFinite(paidDelta) || paidDelta <= 0) return prev;
          const prevPaid = Number(prev.payment_summary?.total_paid ?? prev.total_paid ?? 0);
          const total = Number(prev.payment_summary?.total_amount ?? prev.total_amount ?? 0);
          const nextPaid = prevPaid + paidDelta;
          const remaining = Math.max(0, total - nextPaid);
          return {
            ...prev,
            total_paid: nextPaid,
            remaining_amount: remaining,
            payment_summary: {
              ...(prev.payment_summary || {}),
              total_amount: total,
              total_paid: nextPaid,
              remaining_balance: remaining,
            },
          };
        });
        resetPaymentForm(formSetters);
        setSuccessMessage('Payment collected successfully.');
        setSubmittingPayment(false);
        // Close immediately; parent/list refresh runs in background.
        onPaymentAdded?.();
        onClose?.();
      }
    } catch (err) {
      setPaymentError(err.response?.data?.error || err.message || 'Failed to process payment.');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const renderPaymentClearBanner = () => {
    if (!displayBooking || summary.remainingAmount > 0 || Number(summary.returnAmount) > 0) return null;
    return (
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: isDark ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
          border: `2px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'}`,
          borderRadius: '12px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            }}
          >
            <CheckCircleIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              component="div"
              variant="body2"
              sx={{
                fontWeight: 700,
                color: isDark ? '#81c784' : '#2e7d32',
                fontSize: '0.9rem',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              Payment Clear
              <Chip
                label="Complete"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
                  color: isDark ? '#81c784' : '#2e7d32',
                  border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
                }}
              />
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? drawerTheme.subtleText : '#666',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                display: 'block',
              }}
            >
              All payments have been collected for this booking.
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={getDrawerRootSx()}
        SlideProps={{ direction: 'left' }}
        transitionDuration={{ enter: 350, exit: 250 }}
        PaperProps={{
          sx: {
            ...getBookingDrawerPaperSx(isDark),
            width: BOOKING_DRAWER_WIDTH_SX,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          },
        }}
      >
        <Box ref={drawerRootRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <Box
            sx={{
              borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
              }}
            >
              <PaymentDrawerHeaderTitle
                booking={displayBooking}
                bookingId={safeBookingId}
                paymentHistory={paymentHistory}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={ACCENT}
              />
              <IconButton onClick={handleClose} sx={{ color: drawerTheme.subtleText }}>
                <CloseIcon />
              </IconButton>
            </Box>
            {submittingPayment ? (
              <LinearProgress
                sx={{
                  height: 3,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': { backgroundColor: ACCENT },
                }}
              />
            ) : null}
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {loadingBooking && !displayBooking?.payment_summary ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} sx={{ color: ACCENT }} />
              </Box>
            ) : (
              <>
                {displayBooking ? (
                  <BookingPaymentSummaryCard
                    summary={summary}
                    bookingId={safeBookingId}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                    overlayMountRef={drawerRootRef}
                    onExtraChargesSaved={(result) => {
                      if (result?.booking) {
                        setBooking(result.booking);
                      } else {
                        fetchBooking();
                      }
                      onPaymentAdded?.();
                    }}
                  />
                ) : null}

                {renderPaymentClearBanner()}

                {loadingPayments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, mb: 2 }}>
                    <CircularProgress size={24} sx={{ color: ACCENT }} />
                  </Box>
                ) : (
                  <BookingFinancialHistoryPanel
                    payments={paymentHistory}
                    currency={displayBooking?.currency || 'INR'}
                    hotelTimezone={displayBooking?.hotel_timezone}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                    accent={ACCENT}
                    defaultExpanded={false}
                  />
                )}

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                    border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                    borderRadius: '12px',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: drawerTheme.text, fontWeight: 600, mb: 1.5 }}>
                    Collect Payment
                  </Typography>
                  {paymentError ? (
                    <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setPaymentError('')}>
                      {paymentError}
                    </Alert>
                  ) : null}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Payment Amount"
                        type="number"
                        value={paymentAmount ?? ''}
                        onChange={(e) => {
                          const { value } = e.target;
                          if (value === '') {
                            setPaymentAmount('');
                            return;
                          }
                          const numericValue = Number(value);
                          if (Number.isFinite(numericValue) && numericValue >= 0) {
                            setPaymentAmount(value);
                          }
                        }}
                        inputProps={{ step: '0.01', min: '0' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {displayCurrencySymbol}
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          label="Payment Date & Time"
                          disableFuture
                          value={paymentDate}
                          onChange={setPaymentDate}
                          slotProps={{
                            ...datePickerSlotProps,
                            textField: {
                              fullWidth: true,
                              size: 'medium',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: drawerTheme.subtleText, fontSize: '0.7rem', fontWeight: 600, mb: 1, display: 'block' }}>
                        Payment Method *
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {paymentMethods.map((method) => (
                          <Chip
                            key={method}
                            label={method}
                            onClick={() => {
                              setPaymentMethod(method);
                              setPaymentLinkSendOpen(method === PAYMENT_LINK_METHOD && canSendPaymentLink);
                            }}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: paymentMethod === method
                                ? ACCENT
                                : (isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'),
                              color: paymentMethod === method ? '#000' : ACCENT,
                              border: `1px solid ${paymentMethod === method ? ACCENT : (isDark ? 'rgba(76, 225, 182, 0.3)' : 'rgba(76, 225, 182, 0.25)')}`,
                              fontWeight: paymentMethod === method ? 700 : 600,
                              fontSize: '0.75rem',
                              height: 32,
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Reference Number (Optional)"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: drawerTheme.text }}>
                            Payment Receipt (Optional)
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                            <FormControlLabel
                              label="Camera upload"
                              labelPlacement="start"
                              control={
                                <Switch
                                  checked={paymentCameraUpload}
                                  onChange={(e) => setPaymentCameraUpload(e.target.checked)}
                                  color="primary"
                                />
                              }
                              sx={{ color: drawerTheme.text }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                      <Paper
                        sx={{
                          p: 2,
                          border: `2px dashed ${ACCENT}`,
                          borderRadius: 2,
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                          minHeight: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={handlePaymentReceiptClick}
                      >
                        {paymentReceipt ? (
                          <Box>
                            <img
                              src={paymentReceipt}
                              alt="Payment Receipt"
                              style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                            />
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPaymentReceipt(null);
                              }}
                              sx={{ mt: 1 }}
                            >
                              Remove
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            {paymentCameraUpload ? (
                              <CameraAltIcon sx={{ fontSize: 48, color: drawerTheme.subtleText, mb: 1 }} />
                            ) : (
                              <CloudUploadIcon sx={{ fontSize: 48, color: drawerTheme.subtleText, mb: 1 }} />
                            )}
                            <Typography variant="body2" sx={{ color: drawerTheme.subtleText }}>
                              {paymentCameraUpload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                            </Typography>
                          </Box>
                        )}
                        <input
                          ref={paymentReceiptFileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handlePaymentReceiptUpload}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  disabled={submittingPayment}
                  sx={{
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: drawerTheme.text,
                    '&:hover': { borderColor: ACCENT, color: ACCENT },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmitPayment}
                  disabled={!canUpdatePayment || submittingPayment || !paymentAmount || !paymentMethod || paymentMethod === PAYMENT_LINK_METHOD}
                  sx={{
                    backgroundColor: ACCENT,
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: ACCENT, opacity: 0.9 },
                    '&:disabled': {
                      backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: drawerTheme.subtleText,
                    },
                  }}
                >
                  {submittingPayment ? <CircularProgress size={20} /> : 'Collect Payment'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {canSendPaymentLink ? (
            <PaymentLinkDrawerOverlay
              open={paymentLinkSendOpen}
              onClose={() => setPaymentLinkSendOpen(false)}
              bookingId={safeBookingId}
              booking={displayBooking}
              amount={Number(paymentAmount) || 0}
              currency={displayBooking?.currency || 'INR'}
              isDark={isDark}
              accent={ACCENT}
              canSendEmail={canSendPaymentLinkEmail}
              canSendWhatsApp={canSendPaymentLinkWhatsApp}
              onSentSuccess={(msg) => setSuccessMessage(msg)}
              onAfterSend={async () => {
                const code = booking?.code || booking?.booking_code || bookingContext?.booking_code;
                await fetchPaymentHistory(code);
                await fetchBooking();
              }}
            />
          ) : null}

          {paymentCameraOpen ? (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                zIndex: 1500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) stopPaymentCamera();
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 800, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                  Capture Payment Receipt
                </Typography>
                <Box sx={{ width: '100%', maxWidth: 600, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <video ref={paymentReceiptVideoRef} autoPlay playsInline style={{ width: '100%', height: 'auto', display: 'block' }} />
                  <canvas ref={paymentReceiptCanvasRef} style={{ display: 'none' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="contained" onClick={capturePaymentReceipt} sx={{ backgroundColor: ACCENT, color: '#000' }}>
                    Capture
                  </Button>
                  <Button variant="outlined" onClick={stopPaymentCamera} sx={{ borderColor: 'white', color: 'white' }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ backgroundColor: ACCENT, color: '#000', fontWeight: 600, '& .MuiAlert-icon': { color: '#000' } }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={cameraPermissionHint.open}
        autoHideDuration={9000}
        onClose={() => setCameraPermissionHint({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCameraPermissionHint({ open: false, message: '' })}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {cameraPermissionHint.message}
        </Alert>
      </Snackbar>
    </>
  );
}
